import { useState } from "react";
import { useLocation, useNavigate, type Location } from "react-router";
import { useSetAtom } from "jotai";
import { ArrowRight, ClipboardList, Inbox, LogIn, MessageSquare, Video } from "lucide-react";
import {
  Body,
  Caption,
  Subtitle,
  Title,
  VButton,
  VField,
  VInput,
  cn,
} from "@/components/ui";
import type { V2User } from "@/v2/shared/model";
import { roleIcons, roleLabels } from "@/v2/shared/statusMeta";
import { V2_USERS } from "@/v2/auth/users";
import { v2CurrentUserAtom, v2IsAuthenticatedAtom } from "@/v2/auth/store";

type RedirectState = { from?: Location };

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useSetAtom(v2CurrentUserAtom);
  const setAuthenticated = useSetAtom(v2IsAuthenticatedAtom);
  const state = location.state as RedirectState | null;
  const redirectTo = state?.from?.pathname ?? "/";

  const [selected, setSelected] = useState<V2User>(V2_USERS[0]);
  const [password, setPassword] = useState("demo");

  function handleLogin(): void {
    setUser(selected);
    setAuthenticated(true);
    navigate(redirectTo, { replace: true });
  }

  return (
    <div className="min-h-screen bg-card">
      <div className="grid min-h-screen lg:grid-cols-2">
        <div className="flex items-center justify-center bg-card p-6 sm:p-10">
          <div className="w-full max-w-md space-y-8">
            <img
              src="/pza-logo.jpg"
              alt="Federale Politie en Politiezone Antwerpen"
              className="block h-12 w-auto"
            />

            <div className="space-y-1">
              <Title>Welkom terug</Title>
              <Subtitle>Selecteer een demo-account en log in.</Subtitle>
            </div>

            <div className="space-y-2">
              {V2_USERS.map((user) => {
                const Icon = roleIcons[user.role];
                const isSelected = selected.id === user.id;
                return (
                  <VButton
                    key={user.id}
                    variant="outline"
                    fullWidth
                    onClick={() => setSelected(user)}
                    className={cn(
                      "h-auto justify-start gap-3 px-3 py-2 text-left",
                      isSelected
                        ? "border-primary-600 bg-primary-50 ring-2 ring-primary-200 hover:bg-primary-50"
                        : "hover:border-primary-300 hover:bg-muted/40",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-flex size-9 shrink-0 items-center justify-center rounded-md",
                        isSelected ? "bg-primary-600 text-on-primary" : `${user.avatarColor} text-white`,
                      )}
                    >
                      <Icon className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <Body weight="semibold">
                        {user.firstName} {user.lastName}
                      </Body>
                      <Caption tone="muted" as="p">
                        {roleLabels[user.role]} · {user.service}
                      </Caption>
                    </span>
                    {isSelected ? (
                      <ArrowRight className="size-4 text-primary-700" aria-hidden />
                    ) : null}
                  </VButton>
                );
              })}
            </div>

            <div className="space-y-3">
              <VField label="E-mail" htmlFor="login-email">
                <VInput id="login-email" type="email" value={selected.email} readOnly />
              </VField>
              <VField
                label="Wachtwoord"
                htmlFor="login-password"
                hint="Demo: elk wachtwoord wordt aanvaard."
              >
                <VInput
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </VField>
            </div>

            <VButton fullWidth onClick={handleLogin} disabled={password.length === 0}>
              <LogIn className="size-4" aria-hidden />
              Inloggen als {selected.firstName}
            </VButton>
          </div>
        </div>

        <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-700 via-primary-700 to-primary-800 p-10 text-on-primary lg:flex lg:pl-[136px] lg:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,96px_50%)]">
          <Video
            className="pointer-events-none absolute -bottom-16 -right-16 size-80 text-on-primary/[0.05]"
            strokeWidth={1}
            aria-hidden
          />

          <Caption className="relative uppercase tracking-wide text-on-primary/70">
            Politiezone Antwerpen
          </Caption>

          <div className="relative space-y-8">
            <div className="space-y-3">
              <Title className="text-on-primary">Praesidium</Title>
              <Subtitle className="max-w-md text-on-primary/80">
                Interne tool voor mobiel cameratoezicht: beheer aanvragen, dossiers en plaatsing
                vanuit één centrale omgeving.
              </Subtitle>
            </div>

            <ul className="space-y-3">
              {[
                { icon: ClipboardList, label: "Aanvragen en dossiers in één overzicht" },
                { icon: Inbox, label: "Assets reserveren en inplannen" },
                { icon: MessageSquare, label: "Communicatie en historiek per dossier" },
              ].map((feature) => (
                <li key={feature.label} className="flex items-center gap-3">
                  <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-on-primary/10 ring-1 ring-on-primary/15">
                    <feature.icon className="size-4" aria-hidden />
                  </span>
                  <Body className="text-on-primary/90">{feature.label}</Body>
                </li>
              ))}
            </ul>
          </div>

          <Caption className="relative text-on-primary/60">
            Demo-omgeving, geen echte authenticatie (Entra ID gemockt).
          </Caption>
        </div>
      </div>
    </div>
  );
}
